<template>
    <div class="chat-bar">
        <BaseIconButton class="attach">
            <template #icon>
                <label for="file">
                    <i class="material-symbols-outlined">attach_file</i>
                </label>
            </template>
        </BaseIconButton>

        <div class="message-box">
            <div class="message-box-inner">
                <div v-if="file" class="file-preview-box">
                    <button class="file-preview-remove" @click="removeFile">
                        <i class="material-symbols-outlined">delete</i>
                    </button>

                    <img
                        v-if="isImage"
                        :src="previewUrl"
                        class="file-preview-image"
                        alt="preview"
                    />

                    <div v-else-if="isPdf" class="file-preview-pdf">
                        <i class="material-symbols-outlined pdf-icon">picture_as_pdf</i>
                    </div>

                    <div v-else-if="isDoc" class="file-preview-doc">
                        <i class="material-symbols-outlined doc-icon">description</i>
                    </div>

                    <div v-else-if="isXlsx" class="file-preview-xlsx">
                        <i class="material-symbols-outlined xlsx-icon">table_chart</i>
                    </div>
                </div>

                <textarea
                    maxlength="524288"
                    :placeholder="`${tokens.fullScreenSupport.sendMessage}`"
                    id="support-message"
                    class="body__medium message-textarea"
                    v-model="message"
                    @keyup.enter="sendMessage"
                ></textarea>
            </div>
        </div>

        <div class="send">
            <input type="file" id="file" ref="file" @change="loadPreview">
            <BaseIconButton @click.native="sendMessage">
                <template #icon>
                    <i class="material-symbols-outlined">send</i>
                </template>
            </BaseIconButton>
        </div>
    </div>
</template>

<script>
import {mapGetters} from "vuex"
import {NOTIFICATION_ERROR} from "@/store/mutations.type"
import {ATTACHMENT_UPLOAD, SUPPORT_SEND} from "@/store/actions.type"
import {NEW_CHAT, REST_ENDPOINT} from "@/common/config"
import BaseIconButton from "@/new-design/shared/ui/buttons/BaseIconButton.vue"
import { encodeAngleBrackets } from "@/common/helpers"

export default {
    name: "ChatBar",
    components: {BaseIconButton},
    data() {
        return {
            restEndpoint: REST_ENDPOINT,
            message: '',
            file: null,
            preview: '',
            previewShow: false,
            typeFile: '',
            newChat: NEW_CHAT,
            allowedFileTypes: [
                'image/jpeg',
                'image/png',
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ]
        }
    },
    computed: {
        ...mapGetters(['supportMessages', 'modalFullscreen', 'user', 'tokens', 'staticBase']),

        isImage() {
            return this.file && this.file.type.startsWith("image/")
        },

        isPdf() {
            return this.file && this.file.type === "application/pdf"
        },

        isDoc() {
            return this.file && [
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            ].includes(this.file.type)
        },

        isXlsx() {
            return this.file && this.file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        },

        previewUrl() {
            return this.isImage ? URL.createObjectURL(this.file) : null
        }
    },
    methods: {
        sendMessage(e) {
            if (e.shiftKey) {
                return
            }

            const cleanMessage = this.message.trim()
            const hasFile = this.$refs.file?.files?.[0]

            if (!cleanMessage && !hasFile) {
                return
            }

            if (this.user.enable_chat === 0) {
                this.$store.commit(NOTIFICATION_ERROR, this.tokens.errors.CHAT_DISABLED)
                this.message = ''
                this.removeFile()
                return
            }

            if (cleanMessage.length > 0) {
                this.$store.dispatch(SUPPORT_SEND, encodeAngleBrackets(cleanMessage))
            }

            if (hasFile) {
                const file = this.$refs.file.files[0]
                
                if (this.allowedFileTypes.includes(file.type)) {
                    const data = new FormData()
                    data.append('file', file)
                    this.$store.dispatch(ATTACHMENT_UPLOAD, data)
                } else {
                    this.$store.commit(NOTIFICATION_ERROR, 'LOCAL_INVALID_FILE_TYPE')
                }
            }

            this.message = ''
            this.removeFile()
        },

        loadPreview() {
            const file = this.$refs.file.files[0]
            if (!file) {
                return
            }

            this.file = file
            this.typeFile = file.type

            if (!this.allowedFileTypes.includes(file.type)) {
                this.$store.commit(NOTIFICATION_ERROR, 'LOCAL_INVALID_FILE_TYPE')
                this.removeFile()
                return
            }

            if (file.type.startsWith('image/')) {
                this.preview = URL.createObjectURL(file)
            }

            this.previewShow = true
        },

        removeFile() {
            this.file = null
            this.previewShow = false
            this.preview = ""
            if (this.$refs.file) {
                this.$refs.file.value = ""
            }
        }
    }
}
</script>

<style scoped>
.chat-bar {
    background: var(--System-Surface1);
    display: grid;
    grid-template-columns: max-content 1fr max-content;
    border-top: var(--System-Surface5);
    padding: 16px;
    place-items: center stretch;
}

.message-box {
    margin: 0 10px;
    width: 100%;
}

textarea {
    flex: 1;
    width: 100%;
    height: 52px;
    resize: none;
    border-radius: var(--Corner-small);
    background: var(--System-Background);
    color: var(--System-Outline);
    padding: 16px;
    outline: none;
    border: none;
}

textarea::placeholder {
    color: var(--System-Outline);
}

.send {
    padding: 6px;
    cursor: pointer;
}

#file {
    display: none;
}

.attach {
    width: 48px;
    height: 48px;
}

.attach label {
    cursor: pointer;
}

.message-box {
    width: 100%;
    margin: 0;
}

.message-box-inner {
    display: flex;
    align-items: center;
    gap: 12px;
}

.file-preview-box {
    position: relative;
    background: var(--System-Surface2);
    border-radius: var(--Corner-small);
    padding: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 60px;
    min-width: 60px;
}

.file-preview-image {
    width: 100%;
    height: auto;
    border-radius: 4px;
}

.file-preview-pdf,
.file-preview-doc,
.file-preview-xlsx {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50px;
}

.pdf-icon {
    font-size: 32px;
    color: var(--System-Primary);
}

.file-preview-remove {
    opacity: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: var(--System-Surface3);
    border-radius: 50%;
    padding: 4px;
    border: none;
    cursor: pointer;
    transition: opacity 0.2s ease;
    color: var(--System-OnSurfaceVariant);
}

.file-preview-box:hover .file-preview-remove {
    opacity: 1;
}
</style>